import React, { useEffect, useRef, useState } from "react";

// Loads the YouTube IFrame API once and resolves when ready.
let apiPromise = null;
function loadYouTubeAPI() {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve(window.YT);
    };
    document.head.appendChild(tag);
  });
  return apiPromise;
}

// A YouTube player where the native UI is NOT interactive.
// The <iframe> is a normal (working) embed, but pointer events are disabled
// so YouTube's title link, share and "watch later" buttons cannot be clicked
// and never appear on hover. A transparent overlay only toggles play / pause.
export default function YouTubePlayer({ videoId }) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(true);

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  const src =
    `https://www.youtube.com/embed/${videoId}` +
    `?enablejsapi=1&autoplay=1&mute=0&rel=0&modestbranding=1&controls=0` +
    `&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=1&cc_lang_pref=es` +
    `&playsinline=1&origin=${encodeURIComponent(origin)}`;

  useEffect(() => {
    let cancelled = false;
    loadYouTubeAPI().then((YT) => {
      if (cancelled || !iframeRef.current) return;
      // Attach the API to the already-rendered iframe element.
      playerRef.current = new YT.Player(iframeRef.current, {
        events: {
          onStateChange: (e) => {
            if (e.data === 1) setPlaying(true);
            else if (e.data === 2 || e.data === 0) setPlaying(false);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
        playerRef.current = null;
      } catch (_) {}
    };
  }, [videoId]);

  const togglePlay = () => {
    const p = playerRef.current;
    if (!p || !p.getPlayerState) return;
    const state = p.getPlayerState();
    if (state === 1) {
      p.pauseVideo();
      setPlaying(false);
    } else {
      p.playVideo();
      setPlaying(true);
    }
  };

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
      <iframe
        key={videoId}
        ref={iframeRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        src={src}
        title="Testimonio"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      />

      {/* Transparent click layer: only play / pause */}
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? "Pausar video" : "Reproducir video"}
        className="group absolute inset-0 z-10 flex items-center justify-center focus:outline-none"
      >
        {!playing && (
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-black shadow-xl transition-transform duration-300 group-hover:scale-110">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="ml-1"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        )}
      </button>
    </div>
  );
}
